import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box, Collapse, Grid,
} from '@mui/material';
import { SecurityInventoryCategory } from '@x-guard/xgac-types/xgac';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslate } from 'react-admin';
import { useDebouncedCallback } from 'use-debounce';
import { inventoryCategoryIconMap, InventoryCategoryIconName } from '../../../components/icons/inventoryCategoryIcons';
import { AddItemDialog } from './AddItemDialog';

const VISIBLE_CATEGORY_COUNT = 6;
const CATEGORY_TILE_HEIGHT = 74;
const CATEGORY_GRID_ROW_SPACING = 8;
const CATEGORY_GRID_HOVER_DELAY = 250;

export const CreateItemSection = (props: {
  categories: SecurityInventoryCategory[];
  categoriesLoading: boolean;
  createItemFlowActive: boolean;
  map: L.Map | null;
  onBeforeCreate: () => void;
  onAfterCreate: () => void;
  initialCreateCategoryId?: string | null;
  onInitialCreateCategory?: () => void;
}) => {

  const {
    initialCreateCategoryId, onInitialCreateCategory, categories, categoriesLoading,
    createItemFlowActive, map, onBeforeCreate, onAfterCreate,
  } = props;

  const translate = useTranslate();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categoryGridExpanded, setCategoryGridExpanded] = useState(false);
  const [handledInitialCreateCategoryId, setHandledInitialCreateCategoryId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [mapPlacementActive, setMapPlacementActive] = useState(false);

  const closeAddItemDialog = useCallback(() => {

    setAddDialogOpen(false);
    setCategoryGridExpanded(false);

  }, []);

  const stopMapPlacement = useCallback(() => setMapPlacementActive(false), []);

  const cancelMapPlacement = () => {

    stopMapPlacement();
    closeAddItemDialog();

  };

  const selectedCategory = useMemo(() => {

    return categories.find((c) => c._id === selectedCategoryId) ?? null;

  }, [categories, selectedCategoryId]);

  const handleCategorySelect = (categoryId: string) => {

    setSelectedCategoryId(categoryId);
    setCategoryGridExpanded(true);
    setAddDialogOpen(true);

  };

  const shouldCollapseCategoryGrid = categories.length > VISIBLE_CATEGORY_COUNT;
  const categoryGridOpen = categoryGridExpanded || addDialogOpen || createItemFlowActive;

  const expandCategoryGrid = useDebouncedCallback(() => {

    setCategoryGridExpanded(true);

  }, CATEGORY_GRID_HOVER_DELAY);

  const handlePointerMove = () => {

    if (!shouldCollapseCategoryGrid || categoryGridOpen) return;
    expandCategoryGrid();

  };

  const handlePointerLeave = () => {

    expandCategoryGrid.cancel();
    setCategoryGridExpanded(false);

  };

  useEffect(() => {

    const categoryId = initialCreateCategoryId;
    if (!categoryId) {

      setHandledInitialCreateCategoryId(null);
      return;

    }

    if (handledInitialCreateCategoryId === categoryId) return;

    const exists = categories.some((c) => c._id === categoryId);
    if (!exists) return;

    setHandledInitialCreateCategoryId(categoryId);
    setSelectedCategoryId(categoryId);
    setCategoryGridExpanded(true);
    setAddDialogOpen(true);
    onInitialCreateCategory?.();

  }, [initialCreateCategoryId, categories, onInitialCreateCategory, handledInitialCreateCategoryId]);

  if (categoriesLoading) {

    return null;

  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1, width: '100%',
    }}>
      {mapPlacementActive && (
        <button type="button" className="device-action-button" onClick={cancelMapPlacement}>
          <CancelIcon />
          <span>{translate('resources.buttons.text.stop_placing_item')}</span>
        </button>
      )}
      {!mapPlacementActive && (
        <Box
          aria-expanded={shouldCollapseCategoryGrid ? categoryGridOpen : undefined}
          aria-label={shouldCollapseCategoryGrid ? translate('resources.security-inventory-items.text.show_all_categories') : undefined}
          onPointerEnter={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onFocus={() => setCategoryGridExpanded(true)}
          onBlur={(event) => {

            const nextFocusedElement = event.relatedTarget;
            if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) {

              return;

            }
            setCategoryGridExpanded(false);

          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Collapse
              in={!shouldCollapseCategoryGrid || categoryGridOpen}
              collapsedSize={shouldCollapseCategoryGrid ? CATEGORY_TILE_HEIGHT * 2 + CATEGORY_GRID_ROW_SPACING : 0}
              timeout="auto"
            >
              <Grid container spacing={1}>
                {categories.map((category) => {

                  const Icon = inventoryCategoryIconMap[category.icon as InventoryCategoryIconName];

                  return (
                    <Grid item key={category._id} xs={4}>
                      <button
                        type="button"
                        className="add-device-button"
                        onClick={() => handleCategorySelect(category._id)}
                        style={{
                          height: CATEGORY_TILE_HEIGHT,
                          padding: '8px 6px',
                          justifyContent: 'center',
                          gap: 3,
                        }}
                      >
                        {Icon && <Icon sx={{ fontSize: 22 }} />}
                        <span style={{
                          fontSize: 11,
                          lineHeight: 1.1,
                        }}>
                          {category.name}
                        </span>
                      </button>
                    </Grid>
                  );

                })}
              </Grid>
            </Collapse>
            {shouldCollapseCategoryGrid && (
              <Box sx={{
                backdropFilter: 'blur(2px)',
                background: 'rgba(226, 241, 234, 0.85)',
                bottom: 0,
                height: CATEGORY_TILE_HEIGHT * 0.65,
                left: 0,
                maskImage: 'linear-gradient(to bottom, transparent 0%, black 45%, black 100%)',
                opacity: categoryGridOpen ? 0 : 1,
                WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 45%, black 100%)',
                pointerEvents: 'none',
                position: 'absolute',
                right: 0,
                transition: (theme) => theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.short,
                }),
              }}
              />
            )}
          </Box>
          {shouldCollapseCategoryGrid && (
            <Collapse in={!categoryGridOpen} timeout="auto">
              <Box sx={{
                alignItems: 'center',
                display: 'flex',
                fontSize: 12,
                fontWeight: 700,
                justifyContent: 'center',
                mt: 0.5,
              }}>
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
                <span>{translate('resources.security-inventory-items.text.show_all_categories')}</span>
              </Box>
            </Collapse>
          )}
        </Box>
      )}
      <AddItemDialog
        open={addDialogOpen}
        mapPlacementActive={mapPlacementActive}
        onMapPlacementStart={() => setMapPlacementActive(true)}
        onMapPlacementStop={stopMapPlacement}
        category={selectedCategory}
        map={map}
        onBeforeCreate={onBeforeCreate}
        onAfterCreate={onAfterCreate}
        onClose={closeAddItemDialog}
      />

    </Box>
  );

};
